// @givmo/org-console — the Profile page's PURE model (PE-WEB1): the draft the editor holds, the
// listed-only capacity counters, the id-preserving replace body, the client-side bounds that mirror
// the server's, and the tolerant reads of the two wire shapes.
//
// ══ THE PAGE IS ENTITY-GENERIC BY CONSTRUCTION ═════════════════════════════════════════════════
//
// Every profile-bearing entity authors the same four block kinds into the same store (plan §1.2),
// and the owner view / replace / public read are ONE operation family with a per-plane path. This
// package never learns which plane: the host injects {@link ProfilePageOperations}, and the model
// below reads and writes the wire shapes those operations return, nothing else.
//
// ⚠ The block kind is a TOLERANT enum (plan §1.2): a kind this build does not know is carried
// through the draft untouched and written back exactly as it arrived, so a later kind is
// byte-additive on this client.

import {
  CANONICAL_VIDEO_EMBED_PREFIXES,
  isCanonicalVideoEmbedUrl,
  readPosterUrl,
} from '@givmo/design-system';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import type {
  ProfileBlockInput,
  ProfileBlockOut,
  ProfilePageImageUploadOut,
  ProfilePageOut,
  ProfilePageReplaceIn,
  PublicProfilePageOut,
} from '@givmo/api-client';

// The public composition is the design system's — ONE implementation shared with the donate page's
// public render (D-PE-17 (6)); re-exported here so this package's surface is unchanged.
export {
  composeForRender,
  readPosterUrl,
  videoProviderLabel,
  type ComposeOptions,
  type RenderItem,
} from '@givmo/design-system';
import { PROFILE_COPY } from './profile-copy';

/** The block-kind vocabulary v1 (plan §1.2). */
export const PROFILE_BLOCK_KINDS = ['story', 'image', 'video', 'link'] as const;
export type ProfileBlockKind = (typeof PROFILE_BLOCK_KINDS)[number];

/** The listed-only budgets (plan §1.2 / D-PE-8): a takedown never consumes the page's budget. */
export const MAX_LISTED_BLOCKS = 20;
export const MAX_LISTED_LINKS = 10;
/** The per-kind bounds the server enforces (`ProfileBlockContent`), mirrored so no request is spent on them. */
export const STORY_TITLE_MAX = 200;
export const STORY_BODY_MAX = 5000;
export const IMAGE_CAPTION_MAX = 200;
export const LINK_TITLE_MAX = 100;
export const LINK_URL_MAX = 2048;

/**
 * The two embed hosts the server canonicalizes to — re-checked before an iframe ever mounts (§3.7).
 * ONE list, the design system's: the facade that mounts the frame checks the same constant.
 */
export const VIDEO_EMBED_PREFIXES = CANONICAL_VIDEO_EMBED_PREFIXES;
/** The page hosts a video link may name (the server extracts the id and stores the embed form). */
const VIDEO_PAGE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'vimeo.com',
  'www.vimeo.com',
  'player.vimeo.com',
  'www.youtube-nocookie.com',
]);

/**
 * The operations one host binds — the owner view, the id-preserving replace, the image upload and
 * the public composition — each on that host's own plane. Every one may throw a `GivmoError`; the
 * page reads its `status` (a 404 on the owner read is "not available for this organization").
 */
export interface ProfilePageOperations {
  getOwnerPage: () => Promise<ProfilePageOut>;
  replacePage: (body: ProfilePageReplaceIn) => Promise<ProfilePageOut>;
  uploadImage: (file: File) => Promise<ProfilePageImageUploadOut>;
  getPublicPage: () => Promise<PublicProfilePageOut>;
}

/** The words and links one host supplies. Rendered verbatim; never branched on. */
export interface ProfilePageVocab {
  /**
   * One line for the part of the public page the console cannot show between the header and the
   * blocks (one class's public page: its donation controls). Absent ⇒ nothing is drawn.
   */
  publicPageInterstitial?: string;
  /** The organization's own name, for the page header's chip. */
  orgName?: string;
  /** A one-line hint above a story's text — a host with a fact the story may duplicate names it. */
  storyHint?: string;
  /**
   * Where the organization's REAL public page lives on the web, when this host has one. Absent ⇒
   * the preview states where the page is shown instead ({@link publicPageStatement}).
   */
  publicPageHref?: string | null;
  /** The statement rendered when there is no web address for the public page. */
  publicPageStatement?: string;
}

/**
 * One row of the editor's draft. `key` is a client-only identity (stable across edits and moves);
 * `id` is the server's, present on a row that came off the wire and absent on a new one.
 */
export interface DraftBlock {
  key: string;
  id: string | null;
  kind: string;
  title: string;
  body: string;
  /** video: the URL as typed (the canonical embed form after a save) · link: the https target · image: the SIGNED read URL, for display only. */
  mediaUrl: string;
  /** image: the object KEY the upload returned — what a save sends back, never the signed URL. */
  mediaKey: string | null;
  unlistedAt: string | null;
  unlistedReason: string | null;
  /** video: the stored poster the server resolved at save time (PE-BE9), signed; null until it has one. */
  posterUrl: string | null;
  /**
   * `title` / `body` exactly as read off the wire (null when absent; null on a new row) — what a
   * LOCKED (unlisted) row sends back, so a save never rewrites a block the person cannot edit.
   */
  wireTitle: string | null;
  wireBody: string | null;
}

let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `draft-${keyCounter}`;
}

export function isKnownKind(kind: string): kind is ProfileBlockKind {
  return (PROFILE_BLOCK_KINDS as readonly string[]).includes(kind);
}

/** The owner view → the editor's draft, in the server's order (owner order). */
export function draftFromWire(page: ProfilePageOut): DraftBlock[] {
  return [...page.blocks]
    .sort((a, b) => a.position - b.position)
    .map((block) => draftRowFromWire(block));
}

function draftRowFromWire(block: ProfileBlockOut): DraftBlock {
  return {
    key: nextKey(),
    id: block.id,
    kind: block.kind,
    title: block.title ?? '',
    body: block.body ?? '',
    mediaUrl: block.media_url ?? '',
    mediaKey: block.kind === 'image' ? (block.media_key ?? null) : null,
    unlistedAt: block.unlisted_at ?? null,
    unlistedReason: block.unlisted_reason ?? null,
    posterUrl: block.kind === 'video' ? readPosterUrl(block) : null,
    wireTitle: block.title ?? null,
    wireBody: block.body ?? null,
  };
}

/** A fresh, listed row of one kind. */
export function newDraftBlock(kind: ProfileBlockKind): DraftBlock {
  return {
    key: nextKey(),
    id: null,
    kind,
    title: '',
    body: '',
    mediaUrl: '',
    mediaKey: null,
    unlistedAt: null,
    unlistedReason: null,
    posterUrl: null,
    wireTitle: null,
    wireBody: null,
  };
}

export function isUnlisted(block: Pick<DraftBlock, 'unlistedAt'>): boolean {
  return typeof block.unlistedAt === 'string' && block.unlistedAt.length > 0;
}

/** The listed-only counters the page shows and the caps it judges (plan §1.2). */
export function countListed(rows: readonly DraftBlock[]): { blocks: number; links: number } {
  let blocks = 0;
  let links = 0;
  for (const row of rows) {
    if (isUnlisted(row)) continue;
    if (row.kind === 'link') links += 1;
    else blocks += 1;
  }
  return { blocks, links };
}

/** Whether another row of `kind` fits under the listed caps. */
export function canAddKind(rows: readonly DraftBlock[], kind: ProfileBlockKind): boolean {
  const { blocks, links } = countListed(rows);
  return kind === 'link' ? links < MAX_LISTED_LINKS : blocks < MAX_LISTED_BLOCKS;
}

export function moveRow(rows: readonly DraftBlock[], key: string, delta: -1 | 1): DraftBlock[] {
  const index = rows.findIndex((row) => row.key === key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row!);
  return next;
}

export function removeRow(rows: readonly DraftBlock[], key: string): DraftBlock[] {
  return rows.filter((row) => row.key !== key);
}

export function updateRow(
  rows: readonly DraftBlock[],
  key: string,
  patch: Partial<Pick<DraftBlock, 'title' | 'body' | 'mediaUrl' | 'mediaKey'>>,
): DraftBlock[] {
  return rows.map((row) => (row.key === key ? { ...row, ...patch } : row));
}

/**
 * The block editor's save body: the full block list and NOTHING else. The replace takes two optional
 * parts and leaves the one a body omits untouched (PE-BE14), so a body without `statistics` can never
 * change the page's Statistics choice.
 */
export type ProfileBlocksReplaceBody = Required<Pick<ProfilePageReplaceIn, 'blocks'>>;

/**
 * The id-preserving FULL-list replace body: ids carried back on every row that has one (an unlisted row
 * stays unlisted — moderation is never client input), new rows without, positions from list order.
 * Per kind, exactly the fields the server admits — a story never carries `media_url`, a link never a
 * `body` — and an unknown kind goes back byte-for-byte as it arrived. A LOCKED (unlisted) row goes
 * back exactly as it was read: no trim, no null-for-blank, the image by its KEY.
 */
export function toReplaceBody(rows: readonly DraftBlock[]): ProfileBlocksReplaceBody {
  return { blocks: rows.map((row) => toInput(row)) };
}

/**
 * The page's version as a read served it (PE-BE20): an opaque token, or `null` when that read
 * carried none — a backend from before the version. A save sends it back as `expected_version`
 * ONLY when the read whose content it edits carried one (D-PE-46), so this console never sends a
 * field a backend that predates it would refuse as unknown. Never parsed: the server alone knows
 * what it covers.
 */
export function readPageVersion(page: unknown): string | null {
  if (typeof page !== 'object' || page === null) return null;
  const version = (page as { version?: unknown }).version;
  return typeof version === 'string' && version.length > 0 ? version : null;
}

/** `body` with the version a save is based on, when there is one (never a `null`, never a blank). */
export function withExpectedVersion<T extends object>(
  body: T,
  version: string | null,
): T & { expected_version?: string } {
  return version === null ? body : { ...body, expected_version: version };
}

/** The server's two codes for a save based on a page that has since changed (PE-BE20). */
export const PAGE_CHANGED_CODE = 'profile_page_changed';
export const BLOCK_NOT_ON_PAGE_CODE = 'profile_block_not_on_page';

/** PE-BE13's sentence for a kept block a save elsewhere removed — the fallback for a backend with no code. */
const NOT_ON_PAGE_SENTENCE = /^Block \d+ is not on this page \(unknown id\)\.$/;

/**
 * Whether a refused save means "the page changed while you were editing" (PE-WEB18): the
 * version precondition's 409 (`profile_page_changed`), or PE-BE13's 422 for a kept block that is
 * no longer on the page (`profile_block_not_on_page`). The CODE is read first, before anything
 * that reads the status (the host's Terms refusal is a 409 too, and keeps its own words); a
 * backend that sends no code is recognized by PE-BE13's whole shape — the content refusals' title
 * and the sentence the backend pins — the way the iOS editor does.
 */
export function isStaleSaveRefusal(error: unknown): boolean {
  if (!isGivmoError(error)) return false;
  const code = firstErrorCode(error);
  if (code !== null) return code === PAGE_CHANGED_CODE || code === BLOCK_NOT_ON_PAGE_CODE;
  const entry = error.entries[0];
  return (
    error.status === 422 &&
    entry?.title === 'Invalid profile block' &&
    NOT_ON_PAGE_SENTENCE.test((entry.detail ?? '').trim())
  );
}

/**
 * One row as the replace sends it (exported for Reload page, which compares rows the way the
 * server stores them): a LOCKED (unlisted) row as it was read, any other row as the draft holds it.
 */
export function blockInput(row: DraftBlock): ProfileBlockInput {
  return toInput(row);
}

/** One row as it was read, whatever the draft holds (Reload page's "only taken down" test). */
export function lockedBlockInput(row: DraftBlock): ProfileBlockInput {
  return lockedInput(row, row.id === null ? {} : { id: row.id });
}

function toInput(row: DraftBlock): ProfileBlockInput {
  const id = row.id === null ? {} : { id: row.id };
  // A removed block is LOCKED except delete (D-PE-17 (1)): the person cannot edit it here, so a save
  // of the OTHER rows must not rewrite it either — not even a trimmed title (plan §3.6).
  if (isUnlisted(row)) return lockedInput(row, id);
  const title = row.title.trim();
  switch (row.kind) {
    case 'story':
      return { ...id, kind: 'story', title: title.length > 0 ? title : null, body: row.body };
    case 'image':
      return {
        ...id,
        kind: 'image',
        title: title.length > 0 ? title : null,
        media_url: row.mediaKey ?? row.mediaUrl,
      };
    case 'video':
      return { ...id, kind: 'video', media_url: row.mediaUrl.trim() };
    case 'link':
      return { ...id, kind: 'link', title, media_url: row.mediaUrl.trim() };
    default:
      // A kind this build does not know: written back exactly as it was read.
      return {
        ...id,
        kind: row.kind as ProfileBlockInput['kind'],
        title: title.length > 0 ? title : null,
        body: row.body.length > 0 ? row.body : null,
        media_url: row.mediaKey ?? (row.mediaUrl.length > 0 ? row.mediaUrl : null),
      };
  }
}

/** The wire row of a LOCKED (unlisted) block: its stored values untouched, per kind the admitted fields. */
function lockedInput(row: DraftBlock, id: Pick<ProfileBlockInput, 'id'>): ProfileBlockInput {
  const kind = row.kind as ProfileBlockInput['kind'];
  switch (row.kind) {
    case 'story':
      return { ...id, kind, title: row.wireTitle, body: row.wireBody };
    case 'image':
      return { ...id, kind, title: row.wireTitle, media_url: row.mediaKey ?? row.mediaUrl };
    case 'video':
      return { ...id, kind, media_url: row.mediaUrl };
    case 'link':
      return { ...id, kind, title: row.wireTitle, media_url: row.mediaUrl };
    default:
      return {
        ...id,
        kind,
        title: row.wireTitle,
        body: row.wireBody,
        media_url: row.mediaKey ?? (row.mediaUrl.length > 0 ? row.mediaUrl : null),
      };
  }
}

/** Client-side bounds, mirroring the server's, keyed by row. An empty map is a saveable draft. */
export function validateDraft(rows: readonly DraftBlock[]): Map<string, string> {
  const problems = new Map<string, string>();
  const errors = PROFILE_COPY.blocks.errors;
  for (const row of rows) {
    const title = row.title.trim();
    switch (row.kind) {
      case 'story':
        if (row.body.trim().length === 0) problems.set(row.key, errors.storyBodyRequired);
        else if (row.body.length > STORY_BODY_MAX) problems.set(row.key, errors.storyBodyTooLong);
        else if (title.length > STORY_TITLE_MAX) problems.set(row.key, errors.titleTooLong);
        break;
      case 'image':
        if ((row.mediaKey ?? '').length === 0 && row.mediaUrl.length === 0) {
          problems.set(row.key, errors.imageRequired);
        } else if (title.length > IMAGE_CAPTION_MAX) problems.set(row.key, errors.captionTooLong);
        break;
      case 'video': {
        const url = row.mediaUrl.trim();
        if (url.length === 0) problems.set(row.key, errors.videoRequired);
        else if (!isVideoPageUrl(url)) problems.set(row.key, errors.videoHost);
        break;
      }
      case 'link': {
        const url = row.mediaUrl.trim();
        if (title.length === 0) problems.set(row.key, errors.linkTitleRequired);
        else if (title.length > LINK_TITLE_MAX) problems.set(row.key, errors.linkTitleTooLong);
        else if (url.length === 0) problems.set(row.key, errors.linkUrlRequired);
        else if (!url.startsWith('https://')) problems.set(row.key, errors.linkUrlHttps);
        else if (url.length > LINK_URL_MAX) problems.set(row.key, errors.linkUrlTooLong);
        break;
      }
      default:
        break;
    }
  }
  return problems;
}

/** A YouTube / Vimeo page (or embed) URL, on https. The server canonicalizes; this only pre-checks the host. */
export function isVideoPageUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && VIDEO_PAGE_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

/** A stored embed URL this client will mount an iframe on — re-validated before mounting (§3.7). */
export function isCanonicalEmbedUrl(value: string | null | undefined): boolean {
  return isCanonicalVideoEmbedUrl(value, VIDEO_EMBED_PREFIXES);
}

/**
 * The server names the offending row in its refusal (`Block N: …`, 1-based). Split it so the copy
 * lands under that row, verbatim; a refusal without the prefix is page-level.
 */
export function parseBlockError(message: string): { index: number | null; message: string } {
  const match = /^Block (\d+):\s*(.*)$/s.exec(message.trim());
  if (match === null) return { index: null, message };
  const index = Number(match[1]) - 1;
  return {
    index: Number.isInteger(index) && index >= 0 ? index : null,
    message: match[2] ?? message,
  };
}
